// Animazione tenue dell'immagine di login: il gemello digitale respira e il
// cuore batte. Scelta del 17/10/2026 fra nove varianti provate nel prototipo
// («ciclo completo»): respiro, frequenza cardiaca che segue il respiro, itto a
// ogni sistole e un'onda di perfusione appena accennata.
//
// Tutto sta in un solo fragment shader. Respiro e itto deformano l'immagine;
// l'onda si calcola nelle coordinate del tessuto deformato, cosi' la luce resta
// sopra i vasi veri mentre il torace si allarga. Disegnare l'onda su un livello
// separato la farebbe scivolare fuori dai vasi.
//
// Le coordinate sono pixel di bg.jpg (1218x1197). bg-vessels.webp e' la
// maschera dei vasi ricavata da bg.jpg con scripts/make-login-vessel-mask.py:
// se si cambia l'immagine, va rigenerata, e vanno rivisti HEART e BREATH_C.
//
// Per non pesare piu' del necessario (aggiunte del sito, WEBASTRO !21):
// `deps.maxFps` limita i fotogrammi disegnati, `deps.maxDpr` la risoluzione, e
// fuori dallo schermo il ciclo si ferma. Il respiro e' lento: a 30 fps non si
// distingue da 60, e costa la meta'.
//
// Sullo stesso contesto lungOverlay.js disegna i polmoni (albero bronchiale e
// nuvola di punti, aggiunti il 17/10/2026). Seguono il tessuto: i loro vertex
// shader usano la stessa displace() di questo shader. Se i dati dei polmoni non
// arrivano, il gemello respira lo stesso senza.

import { createLungLayer, parseLungs } from './lungOverlay'

export const IMAGE_SIZE = [1218, 1197]
export const IMAGE_URL = '/assets/images/bg.jpg'
export const VESSELS_URL = '/assets/images/bg-vessels.webp'
export const LUNGS_URL = '/assets/images/bg-lungs.json'

export const HEART = [1045, 705] // dove convergono i vasi
const BREATH_C = [1040, 790] // centro dell'espansione, a meta' torace
const BREATH_R = [330, 290] // raggi dell'ellisse: piu' larga che alta

export const BEAT_PERIOD = 1.0 // 60 bpm a riposo
export const BREATH_PERIOD = 5.0 // 12 atti/min

// Intensita' scelte sul prototipo (1 = valore di riferimento delle varianti):
// col respiro a 2,4 il tessuto al centro del torace si allunga del 13 % a fine
// inspirazione, e meno verso il bordo. `lungs` moltiplica l'opacita' dei
// polmoni.
export const INTENSITY = { breath: 2.4, perfusion: 0.1, itto: 1.0, lungs: 1.3 }

// Aritmia sinusale respiratoria: FC = 60 + 6*sin(wt - phi) bpm, quindi 54-66,
// massima a meta'-fine inspirazione.
const RSA_W = (2 * Math.PI) / BREATH_PERIOD
const RSA_PHI = 0.314
const RSA_DEPTH = 6 / 60

const DUB_DELAY = 0.28 // secondo tono, 280 ms dopo il primo
const WAVE_LIFE = 1.2 // s prima che l'onda si spenga in periferia
const MAX_DT = 0.05 // un frame lungo (scheda in secondo piano) non fa saltare il ciclo
const MAX_DPR = 2

// Inspirazione nel primo 40 % del ciclo, espirazione piu' lenta nel resto.
// 0 = fine espirazione, 1 = fine inspirazione.
export function breathLevel(t) {
  const p = (t % BREATH_PERIOD) / BREATH_PERIOD
  if (p < 0.4) return Math.sin(((Math.PI / 2) * p) / 0.4) ** 2
  return Math.cos(((Math.PI / 2) * (p - 0.4)) / 0.6) ** 2
}

// Tempo dall'ultimo battito (a0) e periodo corrente (period). La fase e'
// l'integrale analitico della frequenza, cosi' e' una funzione pura di t.
export function beatState(t) {
  const phase =
    t +
    (RSA_DEPTH / RSA_W) * (Math.cos(-RSA_PHI) - Math.cos(RSA_W * t - RSA_PHI))
  const rate = 1 + RSA_DEPTH * Math.sin(RSA_W * t - RSA_PHI) // battiti/s
  return { a0: (phase % 1) / rate, period: 1 / rate, bpm: rate * 60 }
}

// Fino a quattro onde attive (primo e secondo tono, del battito corrente e del
// precedente) come terne raggio, spessore, intensita' in pixel immagine.
export function waveRings(a0, period, breath, out = new Float32Array(12)) {
  out.fill(0)
  const boost = 0.7 + 0.6 * breath // onda piu' viva a torace pieno
  const waves = [
    [a0, 1],
    [a0 - DUB_DELAY, 0.45],
    [a0 + period, 1],
    [a0 + period - DUB_DELAY, 0.45],
  ]
  waves.forEach(([age, strength], i) => {
    if (age < 0 || age > WAVE_LIFE) return
    out[i * 3] = 30 + 820 * age
    out[i * 3 + 1] = 70 + 70 * age
    out[i * 3 + 2] = strength * boost * Math.pow(1 - age / WAVE_LIFE, 1.6)
  })
  return out
}

// Sollevamento breve della parete sopra il cuore, picco a 90 ms dalla sistole.
export function systole(a0) {
  return Math.exp(-(((a0 - 0.09) / 0.07) ** 2))
}

// Uniform del frame al tempo t, nell'ordine in cui le legge lo shader.
export function frameUniforms(t, intensity = INTENSITY) {
  const b = breathLevel(t)
  const { a0, period } = beatState(t)
  return {
    breathAmp: 0.055 * intensity.breath * b,
    lift: 4 * intensity.breath * b,
    itto: 0.05 * intensity.itto * systole(a0),
    gain: 0.9 * intensity.perfusion,
    glow: Math.exp(-a0 * 7) * 0.35 * intensity.perfusion,
    rings: waveRings(a0, period, b),
    volume: b,
  }
}

const VERTEX_SHADER = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}'

// Spostamento del respiro e dell'itto nel punto p (pixel immagine): lo shader
// mostra in p il tessuto che sta in p - displace(p). Lo usano anche i vertex
// shader dei polmoni, per muoverli col tessuto.
const DISPLACE_GLSL = `vec2 displace(vec2 p) {
  vec2 q = (p - uC) / uR; float r2 = dot(q, q);
  float wy = smoothstep(430., 640., p.y);
  vec2 d = vec2(uA * (p.x - uC.x), 0.45 * uA * (p.y - uC.y)) * exp(-r2) * wy;
  d.y -= uLift * exp(-r2 * 0.5) * wy;
  vec2 h = p - uH; d += uItto * h * exp(-dot(h, h) / 14400.);
  return d;
}`

const FRAGMENT_SHADER = `precision highp float;
uniform sampler2D uTex, uVes;
uniform vec2 uImg, uC, uR, uH;
uniform float uDpr, uHdev, uOx, uS, uA, uLift, uItto, uGain, uGlow;
uniform vec3 uW[4];
${DISPLACE_GLSL}
void main(){
  vec2 css = vec2(gl_FragCoord.x, uHdev - gl_FragCoord.y) / uDpr;
  vec2 p = (css - vec2(uOx, 0.)) / uS;
  vec2 src = p - displace(p);
  vec3 col = texture2D(uTex, src / uImg).rgb;
  float rh = length(src - uH), wave = 0.;
  for (int i = 0; i < 4; i++) wave += uW[i].z * max(0., 1. - abs(rh - uW[i].x) / uW[i].y);
  vec4 ves = texture2D(uVes, src / uImg);
  col += ves.rgb * ves.a * wave * uGain;
  col += vec3(1., .47, .43) * uGlow * max(0., 1. - rh / 90.);
  gl_FragColor = vec4(col, 1.);
}`

const UNIFORMS = [
  'uTex',
  'uVes',
  'uImg',
  'uC',
  'uR',
  'uH',
  'uDpr',
  'uHdev',
  'uOx',
  'uS',
  'uA',
  'uLift',
  'uItto',
  'uGain',
  'uGlow',
]

function compile(gl, type, source) {
  const shader = gl.createShader(type)
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  return shader
}

function uploadTexture(gl, unit, image, format) {
  gl.activeTexture(gl.TEXTURE0 + unit)
  gl.bindTexture(gl.TEXTURE_2D, gl.createTexture())
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  gl.texImage2D(gl.TEXTURE_2D, 0, format, format, gl.UNSIGNED_BYTE, image)
}

// Programma, triangolo a schermo intero, texture e posizioni delle uniform.
// Restituisce null se il programma non si collega: in quel caso resta l'immagine
// statica del CSS.
function setupProgram(gl, image, vessels) {
  const program = gl.createProgram()
  gl.bindAttribLocation(program, 0, 'a') // la posizione 0 e' condivisa coi polmoni
  gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER))
  gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER))
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null
  gl.useProgram(program)

  const quad = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, quad)
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 3, -1, -1, 3]),
    gl.STATIC_DRAW
  )

  // Niente mipmap: provate sul prototipo, ammorbidiscono l'immagine piu' del
  // rimpicciolimento CSS, e al passaggio statico -> animato si vedrebbe.
  uploadTexture(gl, 0, image, gl.RGB)
  uploadTexture(gl, 1, vessels, gl.RGBA)

  const loc = {}
  UNIFORMS.forEach((name) => {
    loc[name] = gl.getUniformLocation(program, name)
  })
  loc.uW = gl.getUniformLocation(program, 'uW[0]')
  gl.uniform1i(loc.uTex, 0)
  gl.uniform1i(loc.uVes, 1)
  gl.uniform2f(loc.uImg, IMAGE_SIZE[0], IMAGE_SIZE[1])
  gl.uniform2f(loc.uC, BREATH_C[0], BREATH_C[1])
  gl.uniform2f(loc.uR, BREATH_R[0], BREATH_R[1])
  gl.uniform2f(loc.uH, HEART[0], HEART[1])
  return { loc, program, quad }
}

function fetchJson(url) {
  return fetch(url).then((response) =>
    response.ok ? response.json() : Promise.reject(new Error(url))
  )
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = src
  })
}

// Avvia l'animazione sul canvas, che deve coprire esattamente il riquadro con
// bg.jpg in `cover` ancorato in alto a destra. Restituisce la funzione che la
// ferma e libera il contesto. Ogni guasto (niente WebGL, immagini mancanti,
// contesto perso) lascia l'immagine statica sotto e nasconde il canvas.
export function startLivingTwin(canvas, deps = {}) {
  const load = deps.loadImage || loadImage
  const loadJson = deps.fetchJson || fetchJson
  const requestFrame =
    deps.requestFrame || window.requestAnimationFrame.bind(window)
  const cancelFrame =
    deps.cancelFrame || window.cancelAnimationFrame.bind(window)
  const now = deps.now || (() => performance.now())
  const minFrameMs = deps.maxFps ? 1000 / deps.maxFps : 0
  const maxDpr = deps.maxDpr || MAX_DPR

  let gl = null
  let main = null
  let lungs = null
  let layer = null
  let frameId = 0
  let stopped = false
  let started = false
  let t = 0
  let last = 0
  let drawnAt = -Infinity
  let paused = false
  const view = { width: 0, height: 0, dpr: 1, scale: 1, offsetX: 0 }

  const fail = () => {
    stopped = true
    cancelFrame(frameId)
    canvas.hidden = true
  }

  // Lo strato dei polmoni nasce quando ci sono sia il contesto sia i dati,
  // in qualunque ordine arrivino.
  const addLungs = () => {
    if (!gl || !main || !lungs || layer) return
    layer = createLungLayer(gl, lungs, {
      shape: { C: BREATH_C, R: BREATH_R, H: HEART },
      displaceGLSL: DISPLACE_GLSL,
      ...(deps.makeCanvas && { makeCanvas: deps.makeCanvas }),
    })
  }

  const draw = () => {
    if (!view.width || !view.height) return
    const u = frameUniforms(t)
    const { loc } = main
    gl.viewport(0, 0, canvas.width, canvas.height)
    // lo strato dei polmoni cambia programma e attributi: si ripristinano
    gl.useProgram(main.program)
    gl.bindBuffer(gl.ARRAY_BUFFER, main.quad)
    gl.enableVertexAttribArray(0)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
    gl.uniform1f(loc.uDpr, view.dpr)
    gl.uniform1f(loc.uHdev, canvas.height)
    gl.uniform1f(loc.uOx, view.offsetX)
    gl.uniform1f(loc.uS, view.scale)
    gl.uniform1f(loc.uA, u.breathAmp)
    gl.uniform1f(loc.uLift, u.lift)
    gl.uniform1f(loc.uItto, u.itto)
    gl.uniform1f(loc.uGain, u.gain)
    gl.uniform1f(loc.uGlow, u.glow)
    gl.uniform3fv(loc.uW, u.rings)
    gl.drawArrays(gl.TRIANGLES, 0, 3)
    layer?.draw({
      t,
      u,
      intensity: INTENSITY.lungs,
      view: { ...view, width: canvas.width, height: canvas.height },
    })
  }

  const frame = (time) => {
    if (stopped) return
    t += Math.min(MAX_DT, Math.max(0, (time - last) / 1000))
    last = time
    // 1 ms di margine: il rAF a 60 Hz cade a 16,7 ms, e senza margine a
    // 30 fps salterebbe un fotogramma su due a caso.
    if (time - drawnAt >= minFrameMs - 1) {
      drawnAt = time
      draw()
    }
    if (!paused) frameId = requestFrame(frame)
  }

  const start = () => {
    started = true
    Promise.all([load(IMAGE_URL), load(VESSELS_URL)])
      .then(([image, vessels]) => {
        if (stopped) return
        gl = canvas.getContext('webgl', { premultipliedAlpha: false })
        main = gl && setupProgram(gl, image, vessels)
        if (!main) return fail()
        addLungs()
        last = now()
        frameId = requestFrame(frame)
      })
      .catch(fail)
    // I polmoni sono un di piu': se non arrivano il gemello respira senza.
    loadJson(LUNGS_URL)
      .then((json) => {
        lungs = parseLungs(json)
        addLungs()
      })
      .catch(() => {
        lungs = null
      })
  }

  // Le risorse si caricano solo quando il riquadro ha una misura: sotto il
  // breakpoint tablet il pannello e' nascosto e l'animazione non parte.
  const resize = () => {
    const rect = canvas.getBoundingClientRect()
    view.dpr = Math.min(window.devicePixelRatio || 1, maxDpr)
    view.width = rect.width
    view.height = rect.height
    canvas.width = Math.round(rect.width * view.dpr)
    canvas.height = Math.round(rect.height * view.dpr)
    view.scale = Math.max(
      rect.width / IMAGE_SIZE[0],
      rect.height / IMAGE_SIZE[1]
    )
    view.offsetX = rect.width - IMAGE_SIZE[0] * view.scale
    if (!started && view.width > 0 && view.height > 0) start()
  }

  // Senza ResizeObserver (jsdom del portale) si misura una volta sola.
  const Observer = deps.ResizeObserver || window.ResizeObserver
  const observer = Observer ? new Observer(resize) : null
  observer?.observe(canvas)
  canvas.addEventListener('webglcontextlost', fail)
  resize()

  // Fuori dallo schermo non si disegna: il ciclo si ferma e riparte da dove
  // era, senza saltare in avanti nel respiro.
  const Visibility = deps.IntersectionObserver || window.IntersectionObserver
  const visibility = Visibility
    ? new Visibility(([entry]) => {
        const hidden = !entry.isIntersecting
        if (hidden === paused || stopped) return
        paused = hidden
        if (paused) cancelFrame(frameId)
        else if (gl && main) {
          last = now()
          frameId = requestFrame(frame)
        }
      })
    : null
  visibility?.observe(canvas)

  return () => {
    stopped = true
    cancelFrame(frameId)
    observer?.disconnect()
    visibility?.disconnect()
    canvas.removeEventListener('webglcontextlost', fail)
    gl?.getExtension('WEBGL_lose_context')?.loseContext()
  }
}
